export type Fen = string;
export type San = string;

/** FEN piece char — lowercase = black, uppercase = white. chess + checkers. */
export type PieceChar =
  | 'p'
  | 'n'
  | 'b'
  | 'r'
  | 'q'
  | 'k'
  | 'm'
  | 'x'
  | 'P'
  | 'N'
  | 'B'
  | 'R'
  | 'Q'
  | 'K'
  | 'M'
  | 'X';

/**
 * A supported game; the app/API pick rules + renderer by this id.
 *
 * `backgammon` is **ratified but not wired** (2026-10-03, operator-approved):
 * the rules engine and dice-flow adapter live in `@strategy4kids/game-engine`
 * (`backgammon.ts`, `backgammon-adapter.ts`) and deliberately do NOT satisfy
 * `GameAdapter` — backgammon's state has no FEN and `PositionSnapshot` has no
 * place for dice. Registering it in `adapters.ts` is therefore impossible until
 * the board/UI wiring exists, and `GAME_IDS` (what the picker offers) stays
 * chess+checkers until then. Design: `docs/proposals/backgammon-wiring.md`.
 *
 * `bridge` (Mini-Bridge, 2026-10-15; `docs/proposals/mindgames-and-bridge.md`) is
 * added for the same reason and is likewise **not playable online yet**: it has
 * four seats, not two sides, so it is neither a `GameAdapter` nor in `GAME_IDS`,
 * and the api refuses to create or queue it (400) until the multiplayer-table
 * slice exists. It is a single-device game for now.
 *
 * `whist` (2026-10-16; `docs/proposals/whist-and-pirate-bridge.md`) joins `bridge` on the same terms:
 * four seats, single-device, refused online. `SINGLE_DEVICE_GAME_IDS` is the one list the api guards
 * read, so the next such game is one more entry there, not another special case in each route.
 *
 * `blackjack` (2026-10-16; `docs/proposals/web-only-games.md`) is single-device and refused online like the other card games. It is the engine's id
 * only: it is a web-only game, so it appears in no edition's `games` list and no catalog until slice B2, and never in a native build. Its
 * points are a per-game score tally, never a currency.
 *
 * `colour-dash` (2026-10-16; `docs/proposals/rummy-and-matching-cards.md`) is a colour-and-number matching card game, single-device and refused online
 * like the other card games. It is named for what it is: no other game's name or look is used.
 *
 * `mahjong` (2026-10-16; `docs/proposals/mahjong.md`, slice M5) is the four-player Mahjong table game: single-device, refused online, points only. It is an ADULT-edition
 * game (the child edition does not list it); the id exists so the api can refuse it online and refuse it on the child deployment.
 *
 * `pirate` (2026-10-16; `docs/proposals/whist-and-pirate-bridge.md`) is Pirate Bridge: four seats, each for themselves, single-device, refused online.
 *
 * `rummy` (2026-10-16; `docs/proposals/rummy-and-matching-cards.md`) is another single-device card game: 2 to 4 seats, refused online, no stakes.
 *
 * `crazy-eights` and `hearts` (2026-10-16) are the non-gambling card games that replace Blackjack and Poker
 * (`docs/proposals/blackjack-and-poker-arcade.md`, section 7), on the same terms: single-device, refused online.
 *
 * `poker` (Poker Showdown, five-card draw with a score and no wagering) is a web-only game
 * (`docs/proposals/web-only-games.md`): the id exists so the api can refuse it online, but no edition lists it yet.
 */
export type GameId = 'chess' | 'checkers' | 'backgammon' | 'bridge' | 'whist' | 'crazy-eights' | 'hearts' | 'blackjack' | 'poker' | 'mahjong-solitaire' | 'rummy' | 'colour-dash' | 'mahjong' | 'pirate';

/** Games with no online play yet: the api answers 400 for them instead of falling through to chess. */
export const SINGLE_DEVICE_GAME_IDS = ['bridge', 'whist', 'crazy-eights', 'hearts', 'blackjack', 'poker', 'mahjong-solitaire', 'rummy', 'colour-dash', 'mahjong', 'pirate'] as const;
export type SingleDeviceGameId = (typeof SINGLE_DEVICE_GAME_IDS)[number];
export const isSingleDeviceGameId = (value: unknown): value is SingleDeviceGameId =>
  typeof value === 'string' && (SINGLE_DEVICE_GAME_IDS as readonly string[]).includes(value);

export type Turn = 'w' | 'b';

export type GameResult =
  | { kind: 'checkmate'; winner: Turn }
  | { kind: 'stalemate' }
  | { kind: 'insufficient' }
  | { kind: 'fifty-move' }
  | { kind: 'threefold' }
  | { kind: 'draw' }
  | { kind: 'resign'; winner: Turn }
  | { kind: 'agreement' }
  | { kind: 'timeout'; winner: Turn }
  | { kind: 'elimination'; winner: Turn }
  | { kind: 'immobility'; winner: Turn }
  // Backgammon win quality (additive, 2026-10-03). A plain win reuses
  // `elimination`; these two carry the score vocabulary — the loser bore off
  // nothing (gammon), or additionally still had a checker in the winner's home
  // board or on the bar (backgammon). Mapped 1:1 by `toGameResult` in
  // `@strategy4kids/game-engine`; a coach needs to say "you won doubly!", which
  // collapsing into `elimination` would erase at the seam.
  | { kind: 'gammon'; winner: Turn }
  | { kind: 'backgammon'; winner: Turn };

export interface Move {
  readonly from: string;
  readonly to: string;
  readonly san: San;
  readonly color: Turn;
  readonly piece: Lowercase<PieceChar>;
  readonly captured?: PieceChar;
  readonly promotion?: PieceChar;
  readonly flags: string;
}

export interface PositionSnapshot {
  readonly fen: Fen;
  readonly turn: Turn;
  readonly legalMoves: San[];
  readonly result: GameResult | null;
  readonly captured: { w: PieceChar[]; b: PieceChar[] };
}

export interface RobotLevel {
  readonly id: string;
  readonly name: string;
  /** Stockfish UCI_Elo target. */
  readonly elo: number;
  /** Stockfish Skill Level (0-20); the primary lever for "winnable" easy levels. */
  readonly skill: number;
  /** Think time per move in ms; lower = weaker and faster. */
  readonly movetimeMs: number;
}

export const ROBOT_LEVELS = [
  { id: 'kitten', name: 'Kitten', elo: 400, skill: 0, movetimeMs: 60 },
  { id: 'puppy', name: 'Puppy', elo: 800, skill: 3, movetimeMs: 100 },
  { id: 'rookie', name: 'Rookie', elo: 1200, skill: 5, movetimeMs: 180 },
  { id: 'student', name: 'Student', elo: 1600, skill: 8, movetimeMs: 300 },
  { id: 'coach', name: 'Coach', elo: 2000, skill: 12, movetimeMs: 600 },
] as const satisfies readonly RobotLevel[];

export type RobotLevelId = (typeof ROBOT_LEVELS)[number]['id'];

export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

/**
 * The board overlay the player cycles through, in cycle order. `off` leads so
 * a fresh game starts on the hidden state.
 *
 * Naming caveat, taken knowingly: `control` and `territory` are genuinely heat
 * maps — both are derived from where the *pieces* are. `power` is not; it is a
 * property of the coordinates themselves and looks identical in every position,
 * which is what makes it a useful teaching overlay. The alternative is a second
 * sidebar button and two independently-toggled washes on one board, which is the
 * worse outcome for a kid who is seven.
 *
 * This lives in `protocol` because it is a contract the store, the HUD and the
 * 3D board all already had to agree on and each of which had spelled out for
 * itself. The cost of that was invisible: `Hud`'s label was a nested ternary
 * that fell through to "Heat map: Territory" for any value it did not
 * recognise, so a fourth mode would have shipped a power map wearing the
 * territory map's name.
 */
export const HEATMAP_MODES = ['off', 'control', 'territory', 'power'] as const;

/**
 * Derived from the array rather than written beside it, so a mode cannot be
 * added to the cycle without also being typed — and typed without being
 * orderable.
 */
export type HeatmapMode = (typeof HEATMAP_MODES)[number];

export type {
  GameMode,
  GameOpponent,
  GameStatus,
  Side,
  MoveRecord,
  GameSnapshot,
  GameEvent,
  LiveClock,
  LiveEvent,
  LiveClientMessage,
  MatchEvent,
  BackgammonSnapshot,
  BackgammonEndpoint,
  BackgammonStepPayload,
  BackgammonTurnRecord,
  BackgammonCubeState,
  BackgammonCubeOffer,
  BackgammonCubeRecord,
} from './game.js';
